import {
  type DragEvent,
  type MutableRefObject,
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  ChangeEvent,
} from "react";
import { clsx } from "clsx";
import { useOnFilesChange, useStore } from "../state";
import {
  mergeFileLists,
  partitionFiles,
  preventDefault,
  splitByCapacity,
  toFileRejection,
  useOnPastedFiles,
} from "./utils";

import XButton from "../XButton";
import Nav from "./Nav";
import styles from "./Dropzone.module.css";
import { useSnapshot } from "@web-monorepo/hooks";
import { autoTranslate, type TranslatedString } from "@web-monorepo/translation-runtime";
import type { FileInputSource, FileRejection, FilePickerCallbacks } from "../types";

const INPUT_NAME = "classdojo_drop-zone_file-input";
const FORM_DATA_NAME = "file_upload.dropzone";

/**
 * Reports the files that were dropped but never enter the store. Each rejection reason gets its own
 * callback so consumers can word them differently; a file is reported under one reason only.
 */
const reportRejectedFiles = ({
  invalid,
  invalidName,
  tooLarge,
  source,
  allowedFileExtensions,
  onInvalidFileDrop,
  onInvalidFileName,
  onFileTooLarge,
  onFilesRejected,
}: {
  invalid: File[];
  invalidName: File[];
  tooLarge: File[];
  source: FileInputSource;
  allowedFileExtensions: string[];
  onInvalidFileDrop?: (message: TranslatedString) => void;
  onInvalidFileName?: (rejectedFilenames: string[]) => void;
  onFileTooLarge?: (rejectedFilenames: string[]) => void;
  onFilesRejected?: (rejection: FileRejection) => void;
}) => {
  if (invalid.length > 0) {
    onInvalidFileDrop?.(autoTranslate("That file type is unsupported."));
    onFilesRejected?.(toFileRejection("extension", source, invalid, allowedFileExtensions));
  }
  if (invalidName.length > 0) {
    onInvalidFileName?.(invalidName.map((file) => file.name));
    onFilesRejected?.(toFileRejection("bad_filename", source, invalidName, allowedFileExtensions));
  }
  if (tooLarge.length > 0) {
    onFileTooLarge?.(tooLarge.map((file) => file.name));
    onFilesRejected?.(toFileRejection("too_large", source, tooLarge, allowedFileExtensions));
  }
};

const reportCapacityRejections = ({
  duplicates,
  tooMany,
  source,
  allowedFileExtensions,
  onFilesRejected,
}: {
  duplicates: File[];
  tooMany: File[];
  source: FileInputSource;
  allowedFileExtensions: string[];
  onFilesRejected: ((rejection: FileRejection) => void) | undefined;
}) => {
  if (duplicates.length > 0) {
    onFilesRejected?.(toFileRejection("duplicate_name", source, duplicates, allowedFileExtensions));
  }
  if (tooMany.length > 0) {
    onFilesRejected?.(toFileRejection("too_many", source, tooMany, allowedFileExtensions));
  }
};

const commitValidFiles = ({
  store,
  valid,
  allowMultiple,
  maxAllowedFiles,
  source,
  allowedFileExtensions,
  onFilesRejected,
}: {
  store: ReturnType<typeof useStore>;
  valid: File[];
  allowMultiple: boolean;
  maxAllowedFiles: number;
  source: FileInputSource;
  allowedFileExtensions: string[];
  onFilesRejected: ((rejection: FileRejection) => void) | undefined;
}) => {
  if (valid.length === 0) return;

  if (!allowMultiple) {
    store.setFiles(valid);
    return;
  }

  const { duplicates, tooMany } = store.addFiles(valid, maxAllowedFiles);
  reportCapacityRejections({ duplicates, tooMany, source, allowedFileExtensions, onFilesRejected });
};

export default function Dropzone({
  allowedFileExtensions,
  className,
  maxAllowedFiles,
  maxFilenameLength,
  maxFileSizeBytes,
  onCancel,
  onInvalidFileDrop,
  onInvalidFileName,
  onFileTooLarge,
  onMaxFilesReached,
  onPickerOpened,
  onPickerDismissed,
  onFilesRejected,
  prompt,
  children,
  ariaLabel,
}: {
  allowedFileExtensions: string[];
  className?: string;
  maxAllowedFiles: number;
  maxFilenameLength?: number;
  maxFileSizeBytes?: number;
  prompt: ReactNode;
  children: ReactNode;
  ariaLabel?: string;
} & FilePickerCallbacks) {
  const fileInput = useRef<HTMLInputElement>() as MutableRefObject<HTMLInputElement>;
  const store = useStore();
  const snap = useSnapshot(store);

  const allowMultiple = maxAllowedFiles > 1;

  const onFiles = useCallback(
    (source: FileInputSource, filesOrEvent: File[] | ChangeEvent<HTMLInputElement>) => {
      const newFiles = Array.isArray(filesOrEvent) ? filesOrEvent : Array.from(filesOrEvent.target.files ?? []);

      const { valid, invalid, invalidName, tooLarge } = partitionFiles(newFiles, allowedFileExtensions, {
        maxFilenameLength,
        maxFileSizeBytes,
      });
      reportRejectedFiles({
        invalid,
        invalidName,
        tooLarge,
        source,
        allowedFileExtensions,
        onInvalidFileDrop,
        onInvalidFileName,
        onFileTooLarge,
        onFilesRejected,
      });

      const allFiles = [...store.files, ...valid];

      if (!allowMultiple && allFiles.length > 1) {
        onInvalidFileDrop?.(autoTranslate("You can only add up to 1 file."));
        onMaxFilesReached?.(autoTranslate("You can only add up to 1 file."));
        // `maxAllowed` is `store.files.length`, which is 0 for an empty store — so 2+ brand-new
        // files on a fresh single-file picker are all reported as tooMany. Intentional.
        const { duplicates, tooMany } = splitByCapacity(store.files, valid, store.files.length);
        reportCapacityRejections({ duplicates, tooMany, source, allowedFileExtensions, onFilesRejected });
        return;
      }

      if (allowMultiple && allFiles.length > maxAllowedFiles) {
        // Backwards compatibility with old onInvalidFileDrop, but going forward, use onMaxFilesReached
        onInvalidFileDrop?.(
          autoTranslate("You can add up to __maxAllowedFiles__ photos / videos.", {
            maxAllowedFiles: maxAllowedFiles.toString(),
          }),
        );
        onMaxFilesReached?.(
          autoTranslate("You can add up to __maxAllowedFiles__ photos / videos.", {
            maxAllowedFiles: maxAllowedFiles.toString(),
          }),
          allFiles.length - maxAllowedFiles,
        );
      }

      commitValidFiles({
        store,
        valid,
        allowMultiple,
        maxAllowedFiles,
        source,
        allowedFileExtensions,
        onFilesRejected,
      });
    },
    [
      store,
      allowedFileExtensions,
      onInvalidFileDrop,
      maxAllowedFiles,
      maxFilenameLength,
      maxFileSizeBytes,
      onInvalidFileName,
      onFileTooLarge,
      allowMultiple,
      onMaxFilesReached,
      onFilesRejected,
    ],
  );

  const onPastedFiles = useCallback((files: File[]) => onFiles("paste", files), [onFiles]);
  useOnPastedFiles(onPastedFiles);

  const handleDrop = useCallback(
    (event: DragEvent<HTMLFormElement>) => {
      event.preventDefault();
      store.handleDrag("drop");

      onFiles("drop", Array.from(event.dataTransfer?.files ?? []));
    },
    [store, onFiles],
  );

  const handleChange = useCallback((event: ChangeEvent<HTMLInputElement>) => onFiles("browse", event), [onFiles]);

  // syncronize file input with changes in file state
  useOnFilesChange(
    useCallback(
      (files: File[]) => {
        if (fileInput.current) {
          fileInput.current.files = mergeFileLists(files);
        }
      },
      [fileInput],
    ),
  );

  const handleCancel = useCallback(() => {
    if (fileInput.current) {
      fileInput.current.value = "";
      store.files = [];
    }
    onCancel?.();
  }, [store, fileInput, onCancel]);

  // React 18.2 only wires bubble-phase `cancel` for `<dialog>`, so `onCancel` on an input
  // type-checks and never fires; `useEventListener` reads `element` at render time when the ref
  // is still null and would bind to `window`. Hand-rolled below instead.
  const onPickerDismissedRef = useRef(onPickerDismissed);
  useEffect(() => {
    onPickerDismissedRef.current = onPickerDismissed;
  }, [onPickerDismissed]);

  useEffect(() => {
    const input = fileInput.current;
    if (!input) return;

    const handleNativeCancel = () => onPickerDismissedRef.current?.();
    // eslint-disable-next-line @web-monorepo/prefer-use-event-listener -- target is the ref-resolved <input> node; useEventListener reads `element` at render time when the ref is still null and would bind to window
    input.addEventListener("cancel", handleNativeCancel);
    // eslint-disable-next-line @web-monorepo/prefer-use-event-listener -- pairs with the ref-resolved addEventListener above
    return () => input.removeEventListener("cancel", handleNativeCancel);
  }, []);

  const shouldShowPrompt = snap.files.length === 0;

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        fileInput.current?.click();
      }
    },
    [fileInput],
  );

  return (
    <form
      id="file-upload-form"
      data-name={FORM_DATA_NAME}
      // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
      name={INPUT_NAME}
      onDragEnter={() => store.handleDrag("enter")}
      onDragLeave={() => store.handleDrag("leave")}
      onDragOver={preventDefault}
      onDrop={handleDrop}
      className={clsx(styles.main, { [styles.dragging]: snap.isDragging }, className)}
    >
      {shouldShowPrompt ? (
        <label data-name="file_upload.dropzone.prompt" className={styles.promptContainer} htmlFor={INPUT_NAME}>
          {onCancel && <XButton className={styles.cancelButton} onClick={handleCancel} />}
          <div
            // eslint-disable-next-line @web-monorepo/no-jsx-role-button
            role="button"
            tabIndex={0}
            className={styles.prompt}
            onKeyDown={handleKeyDown}
          >
            {prompt}
          </div>
        </label>
      ) : (
        <>
          <label htmlFor={INPUT_NAME} className={styles.visuallyHidden}>
            {ariaLabel || "Upload files"}
          </label>
          <div className={styles.children}>
            <Nav
              maxAllowedFiles={maxAllowedFiles}
              onCancel={onCancel ? handleCancel : undefined}
              fileInputName={INPUT_NAME}
            />
            {children}
          </div>
        </>
      )}
      <input
        disabled={snap.files.length >= maxAllowedFiles}
        multiple={allowMultiple}
        accept={allowedFileExtensions.join(",")}
        onChange={handleChange}
        onClick={() => onPickerOpened?.()}
        ref={fileInput}
        type="file"
        className={clsx(styles.input, {
          [styles.inputHidden]: !shouldShowPrompt,
        })}
        name={INPUT_NAME}
        id={INPUT_NAME}
      />
    </form>
  );
}
